import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Bot, CircleUser, Clock } from 'lucide-react';
import { Spinner } from '../ui/Spinner';
import { Avatar } from '../ui/Avatar';
import { useTicketActivityLog } from '../../hooks/queries/useTickets';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import type { TicketActivityWithActor } from '../../services/tickets/ticketService';
import type { Json } from '../../types/database.types';

function getActorStyle(actorType: string): { classes: string; Icon: React.ComponentType<{ className?: string }> } {
  switch (actorType) {
    case 'collaborator':
      return {
        classes: 'bg-cime-primary text-white',
        Icon: CircleUser,
      };
    case 'ai':
      return {
        classes: 'bg-cime-gold text-white',
        Icon: Bot,
      };
    default:
      return {
        classes: 'bg-neutral-300 text-neutral-700',
        Icon: Clock,
      };
  }
}

function parseDetails(details: Json): Record<string, unknown> {
  if (typeof details === 'object' && details !== null && !Array.isArray(details)) {
    return details as Record<string, unknown>;
  }
  return {};
}

function getEventDescription(log: TicketActivityWithActor): string {
  const d = parseDetails(log.details);

  switch (log.action) {
    case 'ticket_created_from_webhook':
      return 'Demande créée depuis le formulaire web';
    case 'assigned': {
      return 'Assigné à un collaborateur';
    }
    case 'unassigned':
      return "Assignation retirée";
    case 'status_changed': {
      const statusLabels: Record<string, string> = {
        new: 'Nouveau',
        assigned: 'Assigné',
        in_progress: 'Pris en charge',
        closed: 'Clos',
        spam: 'Spam',
      };
      const from = statusLabels[String(d.from)] ?? String(d.from);
      const to = statusLabels[String(d.to)] ?? String(d.to);
      return `Statut passé de « ${from} » à « ${to} »`;
    }
    case 'priority_changed': {
      const priorityLabels: Record<string, string> = {
        low: 'Basse',
        normal: 'Normale',
        high: 'Haute',
        urgent: 'Urgente',
      };
      const p = priorityLabels[String(d.priority)] ?? String(d.priority);
      return `Priorité passée à « ${p} »`;
    }
    case 'referent_cleared_out_of_competence':
      return "Avocat du formulaire retiré : le domaine identifié sort de ses compétences — demande remise en file d'attente";
    case 'referent_set':
      return d.auto === true
        ? "Avocat référent désigné automatiquement (avocat en charge, associé)"
        : 'Avocat référent modifié';
    case 'email_sent':
      return 'Email envoyé';
    case 'note_added':
      return 'Note interne ajoutée';
    case 'ai_classified': {
      const confidence = typeof d.confidence === 'number' ? Math.round(d.confidence * 100) : null;
      return confidence !== null
        ? `Classé automatiquement par l'IA (${confidence}%)`
        : "Classé automatiquement par l'IA";
    }
    default:
      return log.action.replace(/_/g, ' ');
  }
}

interface TicketTimelineProps {
  ticketId: string;
}

export function TicketTimeline({ ticketId }: TicketTimelineProps) {
  const { data: logs, isLoading } = useTicketActivityLog(ticketId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <p className="text-sm text-neutral-400 italic py-2">Aucune activité enregistrée</p>
    );
  }

  return (
    <ol className="relative border-l border-neutral-200 ml-4 space-y-6">
      {logs.map((log) => {
        const { classes, Icon } = getActorStyle(log.actor_type);
        const description = getEventDescription(log);
        const actorName =
          log.actor_type === 'collaborator'
            ? (log.actor?.full_name ?? 'Collaborateur')
            : log.actor_type === 'ai'
              ? 'Assistant IA'
              : 'Système';

        const timeAgo = formatDistanceToNow(new Date(log.created_at), {
          addSuffix: true,
          locale: fr,
        });

        return (
          <li key={log.id} className="ml-6">
            {/* Timeline dot */}
            <span
              className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${classes}`}
            >
              {log.actor_type === 'collaborator' && log.actor ? (
                log.actor_id ? (
                  <CollaboratorLink collaboratorId={log.actor_id} title={`Voir le profil de ${log.actor.full_name}`}>
                    <Avatar
                      name={log.actor.full_name}
                      src={log.actor.avatar_url ?? undefined}
                      size="xs"
                    />
                  </CollaboratorLink>
                ) : (
                  <Avatar
                    name={log.actor.full_name}
                    src={log.actor.avatar_url ?? undefined}
                    size="xs"
                  />
                )
              ) : (
                <Icon className="w-3 h-3" aria-hidden />
              )}
            </span>

            <div className="ml-1">
              <p className="text-sm text-neutral-800">{description}</p>
              <p className="text-xs text-neutral-400 mt-0.5">
                {log.actor_type === 'collaborator' && log.actor_id ? (
                  <CollaboratorLink collaboratorId={log.actor_id} title={`Voir le profil de ${actorName}`}>{actorName}</CollaboratorLink>
                ) : actorName} · {timeAgo}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
